/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import * as jwt_decode from 'jwt-decode';

/**
 * Manages JWT tokens for customer API and data access.
 * Breaks the circular dependency between AuthService and services that only need auth headers.
 * Services that need authentication can inject TokenService instead of the full AuthService.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService {
  /** Customer API JWT access token */
  token: string;

  /** Data access JWT token for data-related APIs */
  tokenDataAccess: string;

  /** Whether JWT authorization has been verified */
  jwtAuthorized = false;

  /** Whether the ET-token probe has already run this SPA session */
  etTokenProbed = false;

  /** Whether to use localStorage for token persistence */
  private useStorage = false;

  /** Whether the user is in a reader/widget context (uses in-memory token instead of localStorage) */
  private isReaderContext = false;

  /**
   * Configure storage behavior. Called once during app initialization.
   */
  configure(options: { useStorage: boolean; isReaderContext: boolean }): void {
    this.useStorage = options.useStorage;
    this.isReaderContext = options.isReaderContext;
  }

  /**
   * Store tokens and optionally persist to localStorage.
   */
  setTokens(token: string, dataAccessToken?: string): void {
    this.token = token;
    if (dataAccessToken) {
      this.tokenDataAccess = dataAccessToken;
    }
    if (this.useStorage) {
      localStorage.setItem('token', this.token);
      if (dataAccessToken) {
        localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
      }
    }
  }

  /**
   * Get the current active token (from memory or localStorage depending on context).
   */
  getActiveToken(): string | null {
    if (this.isReaderContext) {
      return this.token;
    }
    return localStorage.getItem('token') || this.token;
  }

  /**
   * Decode a JWT token.
   */
  decodeToken(token?: string): any {
    const t = token || this.token;
    if (!t) return null;
    return jwt_decode(t);
  }

  /**
   * Clear all tokens (called on logout).
   */
  clear(): void {
    this.token = undefined;
    this.tokenDataAccess = undefined;
    this.jwtAuthorized = false;
    this.etTokenProbed = false;
    if (this.useStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenDataAccess');
    }
  }
}
